import { LoggingChannels, LoggingService } from '../logging/logging.service';
import { FeatureName, SupportService } from '../support/support.service';
import { Blur } from './blur';
import { FilesetResolver, ImageSegmenter, ImageSegmenterResult } from '@mediapipe/tasks-vision';

export interface VBMaskProcessorState {
  blurred: boolean;
  backgroundImg: boolean;
  backgroundImage: HTMLImageElement;
  blurDensityState: number;
  isActive: boolean;
  resolution: number;
}

export class VBMaskProcessor {
  public segmenter: ImageSegmenter;
  public video: HTMLVideoElement;
  public canvas: HTMLCanvasElement;
  public context: CanvasRenderingContext2D;
  public offscreenCanvas: HTMLCanvasElement;
  public offscreenContext: CanvasRenderingContext2D;
  public blurCanvas: HTMLCanvasElement;
  public blurContext: CanvasRenderingContext2D;
  public maskCanvas: HTMLCanvasElement;
  public maskContext: CanvasRenderingContext2D;
  public blur: Blur;
  public isRunning: boolean;
  public showMask: boolean;
  public certaintyThreshold: number;

  constructor(
    private logging: LoggingService,
    private support: SupportService,
    private stateGetter: () => VBMaskProcessorState,
    private setActive: (active: boolean) => void,
  ) {}

  public async createSegmenter(): Promise<void> {
    const model = 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_multiclass_256x256/float32/latest/selfie_multiclass_256x256.tflite';
    if (this.segmenter) {
      try {
        this.segmenter.close();
      } catch (e) {}
      this.segmenter = null;
    }
    try {
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );
      this.segmenter = await ImageSegmenter.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: model,
          delegate: this.support.isFeatureSupported(FeatureName.WebGL) ? 'GPU' : 'CPU',
        },
        runningMode: 'IMAGE',
        outputCategoryMask: false,
        outputConfidenceMasks: true,
      });
      this.logging.log('VirtualBackgroundService: segmenter ready', LoggingChannels.VirtualBackground);
    } catch(err) {
      this.logging.warning('VirtualBackgroundService: segmenter error', err.message);
    }
  }

  public async handleBlur(): Promise<void> {
    if (this.isRunning) {
      return;
    }

    if (!this.segmenter) {
      this.isRunning = true;
      await this.createSegmenter();
      this.isRunning = false;
      return;
    }

    const state = this.stateGetter();

    if (this.video && this.video.videoWidth > 0 && document.body.contains(this.video)) {
      this.video.width = this.video.videoWidth;
      this.video.height = this.video.videoHeight;
      if (this.canvas) {
        try {
          this.isRunning = true;
          this.offscreenCanvas.width = this.video.width;
          this.offscreenCanvas.height = this.video.height;
          this.blur.contexts.mask.canvas.width = this.maskCanvas.width = Math.floor(this.offscreenCanvas.width * state.resolution);
          this.blur.contexts.mask.canvas.height = this.maskCanvas.height = Math.floor(this.offscreenCanvas.height * state.resolution);
          this.blur.contexts.blur.canvas.width = this.blurCanvas.width = Math.floor(this.maskCanvas.width / 2);
          this.blur.contexts.blur.canvas.height = this.blurCanvas.height = Math.floor(this.maskCanvas.height / 2);
          // Draw the current frame to get access to its image data
          this.offscreenContext.drawImage(this.video, 0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);
          this.maskContext.drawImage(this.video, 0, 0, this.maskCanvas.width, this.maskCanvas.height);
          this.blurContext.drawImage(this.video, 0, 0, this.blurCanvas.width, this.blurCanvas.height);
          // Segment the current frame to extract the mask of the person
          this.segmenter.segment(this.maskCanvas, this.processBlur.bind(this));
        } catch(err) {
          this.logging.log('VirtualBackgroundService: handleBlur error', LoggingChannels.VirtualBackground, err.message);
          await this.createSegmenter();
          this.isRunning = false;
        }
      } else {
        this.canvas = document.getElementById('home-video-canvas') as HTMLCanvasElement;
        this.context = this.canvas.getContext('2d');
        this.maskCanvas = document.createElement('canvas');
        this.maskContext = this.maskCanvas.getContext('2d', { willReadFrequently: true });
        this.blur.initialize('mask');
        this.blurCanvas = document.createElement('canvas');
        this.blurContext = this.blurCanvas.getContext('2d', { willReadFrequently: true });
        this.blur.initialize('blur');
      }
    } else {
      this.video = document.getElementById('home-video') as HTMLVideoElement;
      this.canvas = null;
    }
  }

  public async handleBackgroundImg(): Promise<void> {
    if (this.isRunning) {
      return;
    }

    if (!this.segmenter) {
      this.isRunning = true;
      await this.createSegmenter();
      this.isRunning = false;
      return;
    }

    const state = this.stateGetter();

    if (this.video && this.video.videoWidth > 0 && document.body.contains(this.video)) {
      this.video.width = this.video.videoWidth;
      this.video.height = this.video.videoHeight;
      if (this.canvas) {
        try {
          this.isRunning = true;
          this.offscreenCanvas.width = this.video.width;
          this.offscreenCanvas.height = this.video.height;
          this.blur.contexts.mask.canvas.width = this.maskCanvas.width = Math.floor(this.offscreenCanvas.width * state.resolution);
          this.blur.contexts.mask.canvas.height = this.maskCanvas.height = Math.floor(this.offscreenCanvas.height * state.resolution);
          // Draw the current frame to get access to its image data
          this.offscreenContext.drawImage(this.video, 0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);
          this.maskContext.drawImage(this.video, 0, 0, this.maskCanvas.width, this.maskCanvas.height);
          // Segment the current frame to extract the mask of the person
          this.segmenter.segment(this.maskCanvas, this.processBackgroundImg.bind(this));
        } catch(err) {
          this.logging.log('VirtualBackgroundService: handleBackgroundImg error', LoggingChannels.VirtualBackground, err.message);
          await this.createSegmenter();
          this.isRunning = false;
        }
      } else {
        this.canvas = document.getElementById('home-video-canvas') as HTMLCanvasElement;
        this.context = this.canvas.getContext('2d');
        this.maskCanvas = document.createElement('canvas');
        this.maskContext = this.maskCanvas.getContext('2d', { willReadFrequently: true });
        this.blur.initialize('mask');
      }
    } else {
      this.video = document.getElementById('home-video') as HTMLVideoElement;
      this.canvas = null;
      this.context = null;
    }
  }

  public drawMask(result: ImageSegmenterResult): void {
    if (!this.showMask) {
      const mask = result.confidenceMasks[0].getAsUint8Array();
      const maskImage = this.maskContext.getImageData(0, 0, this.maskCanvas.width, this.maskCanvas.height);
      const maskData = maskImage.data;
      const threshold = this.certaintyThreshold * 255;
      for (let i = 0; i < mask.length; i++) {
        if (mask[i] > threshold) {
          maskData[i * 4] = 0;
          maskData[i * 4 + 1] = 0;
          maskData[i * 4 + 2] = 0;
          maskData[i * 4 + 3] = 0;
        } else {
          maskData[i * 4] = 255;
          maskData[i * 4 + 1] = 0;
          maskData[i * 4 + 2] = 0;
          maskData[i * 4 + 3] = 255;
        }
      }
      this.blur.blurMask(maskImage);
      this.offscreenContext.globalCompositeOperation = 'destination-in';
      this.offscreenContext.drawImage(this.blur.contexts.mask.canvas, 0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);
    } else {
      const mask = result.confidenceMasks[0].getAsUint8Array();
      const maskImage = this.maskContext.getImageData(0, 0, this.maskCanvas.width, this.maskCanvas.height);
      const maskData = maskImage.data;
      const threshold = this.certaintyThreshold * 255;
      for (let i = 0; i < mask.length; i++) {
        if (mask[i] > threshold) {
          maskData[i * 4] = 0;
          maskData[i * 4 + 1] = 0;
          maskData[i * 4 + 2] = 0;
          maskData[i * 4 + 3] = 0;
        } else {
          maskData[i * 4] = 255;
          maskData[i * 4 + 1] = 0;
          maskData[i * 4 + 2] = 0;
          maskData[i * 4 + 3] = 127;
        }
      }
      this.blur.blurMask(maskImage);
      this.offscreenContext.globalCompositeOperation = 'source-over';
      this.offscreenContext.drawImage(this.blur.contexts.mask.canvas, 0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);
    }
  }

  public async processBlur(result: ImageSegmenterResult): Promise<void> {
    const state = this.stateGetter();
    try {
      this.drawMask(result);
      if (!this.showMask) {
        // Draw the background over the masked frame
        const blurData = this.blurContext.getImageData(0, 0, this.blurCanvas.width, this.blurCanvas.height);
        this.blur.blur('blur', blurData, state.blurDensityState);
        this.offscreenContext.globalCompositeOperation = 'destination-over';
        this.offscreenContext.drawImage(this.blur.contexts.blur.canvas, 0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);
      }
      this.canvas.width = this.offscreenCanvas.width;
      this.canvas.height = this.offscreenCanvas.height;
      this.context.drawImage(this.offscreenCanvas, 0, 0);
      this.setActive(true);
    } catch(e) {}
    finally {
      this.isRunning = false;
    }
  }

  public async processBackgroundImg(result: ImageSegmenterResult): Promise<void> {
    const state = this.stateGetter();
    try {
      this.drawMask(result);
      if (!this.showMask) {
        // Draw the background over the masked frame
        this.offscreenContext.globalCompositeOperation = 'destination-over';
        const resizeX = state.backgroundImage.width / this.offscreenCanvas.width;
        const resizeY = state.backgroundImage.height / this.offscreenCanvas.height;
        const resize = 1 / Math.min(resizeX, resizeY);
        const width = Math.floor(state.backgroundImage.width * resize);
        const height = Math.floor(state.backgroundImage.height * resize);
        const x = Math.floor((this.offscreenCanvas.width - state.backgroundImage.width * resize) / 2);
        const y = Math.floor((this.offscreenCanvas.height - state.backgroundImage.height * resize) / 2);
        this.offscreenContext.drawImage(state.backgroundImage, 0, 0, state.backgroundImage.width, state.backgroundImage.height, x, y, width, height);
      }
      this.canvas.width = this.offscreenCanvas.width;
      this.canvas.height = this.offscreenCanvas.height;
      this.context.drawImage(this.offscreenCanvas, 0, 0);
      this.setActive(true);
    } catch(e) {}
    finally {
      this.isRunning = false;
    }
  }

  public clearCanvases(): void {
    if (this.context && this.canvas) {
      this.context.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    this.canvas = null;
    this.context = null;
    if (this.offscreenContext && this.offscreenCanvas) {
      this.offscreenContext.clearRect(0, 0, this.offscreenCanvas.width, this.offscreenCanvas.height);
    }
    if (this.blurContext && this.blurCanvas) {
      this.blurContext.clearRect(0, 0, this.blurCanvas.width, this.blurCanvas.height);
    }
    this.blurCanvas = null;
    this.blurContext = null;
    if (this.maskContext && this.maskCanvas) {
      this.maskContext.clearRect(0, 0, this.maskCanvas.width, this.maskCanvas.height);
    }
    this.maskCanvas = null;
    this.maskContext = null;
    this.video = null;
  }
}
